import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Copy, Share2, Check, Heart } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_0f63188c-f9dd-4eab-a2e3-7399e9175461/artifacts/oetdot0e_SalesAssets%20Logo.png";

const ShareReferral = () => {
  const { token } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/referrals/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReferralData(response.data);
      } catch (error) {
        console.error('Failed to fetch referral data:', error);
        toast.error('Failed to load referral information');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchReferralData();
    }
  }, [token]);

  const handleCopyLink = async () => {
    if (!referralData?.referral_url) return;
    
    try {
      await navigator.clipboard.writeText(referralData.referral_url);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleShare = async () => {
    if (!referralData?.referral_url) return;
    
    const shareData = {
      title: 'SalesAssets',
      text: 'This app turns your job photos into posts that actually get enquiries. Worth a look.',
      url: referralData.referral_url,
    };

    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (error.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center">
        <div className="animate-pulse text-[#0a2540]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/library" 
              className="text-slate-500 hover:text-[#0a2540] transition-colors"
              data-testid="back-to-library-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <img 
                src={LOGO_URL} 
                alt="SalesAssets" 
                className="h-8 w-8 rounded-lg object-cover"
              />
              <span className="font-bold text-[#0a2540]">Share</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#d4af37]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-[#d4af37]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0a2540] mb-2">
            Know a tradesperson who'd find this useful?
          </h1>
          <p className="text-slate-500">
            Share your link — they'll get their first post free.
          </p>
        </div>

        {/* Referral Link Card */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Your link
          </label>
          <div className="flex gap-2">
            <div className="flex-1 bg-[#f8f9fa] border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-600 truncate font-mono">
              {referralData?.referral_url || 'Loading...'}
            </div>
            <Button
              onClick={handleCopyLink}
              variant="outline"
              className="px-4 border-slate-200 hover:bg-slate-50"
              data-testid="copy-referral-link-btn"
            >
              {copied ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>

          {/* Share Button */}
          <Button
            onClick={handleShare}
            className="w-full mt-4 bg-[#d4af37] hover:bg-[#c5a028] text-[#0a2540] font-semibold"
            data-testid="share-referral-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share with a mate
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ShareReferral;
